import type {
  AccessTokenDTO,
  JWTSessionDTO,
} from '@/queries/auth/auth.schema';
import { jwtDecode } from 'jwt-decode';

import { destroyCookie, parseCookies, setCookie } from 'nookies';
import { logoutUser } from '@/libs/auth/logoutUser';
import { AUTH_COOKIE_NAMES } from './token-manager.keys';
import { userApi } from './user/user.api';

// Re-export for backward compatibility
export { AUTH_COOKIE_NAMES };
export type { AuthCookieName } from './token-manager.keys';

const COOKIE_OPTIONS = {
  httpOnly: false,
  secure: true,
  sameSite: 'lax' as const,
  path: '/',
  domain: '.sweepking.com', // Env.NEXT_PUBLIC_ENVIRONMENT === 'production' ? '.sweepking.com' : ''
};

const COOKIE_OPTIONS_OUTDATED = {
  httpOnly: false,
  secure: true,
  sameSite: 'none' as const,
  path: '/',
  domain: '.sweepking.com', // Env.NEXT_PUBLIC_ENVIRONMENT === 'production' ? '.sweepking.com' : ''
};

interface TokenError extends Error {
  code: 'TOKEN_EXPIRED' | 'TOKEN_INVALID' | 'TOKEN_MISSING';
}

class TokenError extends Error {
  constructor(code: TokenError['code'], message: string) {
    super(message);
    this.code = code;
    this.name = 'TokenError';
  }
}

// Token management functions
export const tokenManager = {
  /**
   * Persist auth tokens in cookies.
   *
   * Tries the primary cookie settings first (`COOKIE_OPTIONS`).
   * If cookies are not readable right after setting (e.g. due to browser policy),
   * it automatically retries with the alternative settings (`COOKIE_OPTIONS_2`).
   *
   * Callers do not need to choose between variants – this function handles it.
   */
  setTokens: (tokens: JWTSessionDTO) => {
    try {
      // Validate tokens before setting
      if (!tokens.accessToken || !tokens.refreshToken) {
        throw new TokenError('TOKEN_INVALID', 'Invalid token data provided');
      }

      // Define refresh token expiration in seconds (30 days)
      const REFRESH_TOKEN_EXPIRY = 30 * 24 * 60 * 60; // 30 days in seconds

      // Decode token to validate its structure
      // const decoded = jwtDecode<AccessTokenDTO>(tokens.accessToken);
      // const decodedRefresh = jwtDecode<AccessTokenDTO>(tokens.refreshToken);

      const expiresAt = tokens.expiresIn; // in seconds

      // Validate that access token expiration doesn't exceed refresh token expiration
      if (expiresAt > REFRESH_TOKEN_EXPIRY) {
        console.warn('Access token expiration exceeds refresh token expiration');
      }

      const trySetTokensWithOptions = (options: typeof COOKIE_OPTIONS | typeof COOKIE_OPTIONS_OUTDATED) => {
        // Set cookies with proper expiration
        setCookie(
          null,
          AUTH_COOKIE_NAMES.accessToken,
          tokens.accessToken,
          {
            ...options,
            maxAge: expiresAt, // Use the same expiration time in seconds, currently 5 minutes
          },
        );

        setCookie(
          null,
          AUTH_COOKIE_NAMES.refreshToken,
          tokens.refreshToken,
          {
            ...options,
            maxAge: REFRESH_TOKEN_EXPIRY,
          },
        );

        setCookie(
          null,
          AUTH_COOKIE_NAMES.tokenExpiry,
          expiresAt.toString(),
          {
            ...options,
            maxAge: expiresAt, // Use the same expiration time
          },
        );

        // Verify that cookies are actually readable with these settings
        const cookies = parseCookies();
        const hasAccessToken = !!cookies[AUTH_COOKIE_NAMES.accessToken];
        const hasRefreshToken = !!cookies[AUTH_COOKIE_NAMES.refreshToken];
        const hasTokenExpiry = !!cookies[AUTH_COOKIE_NAMES.tokenExpiry];

        return hasAccessToken && hasRefreshToken && hasTokenExpiry;
      };

      // First, try with primary cookie options
      if (trySetTokensWithOptions(COOKIE_OPTIONS)) {
        return true;
      }

      // If that didn't work, fall back to the alternative options
      if (trySetTokensWithOptions(COOKIE_OPTIONS_OUTDATED)) {
        return true;
      }

      return false;
    } catch {
      // console.error("Error setting tokens:", error);
      return false;
    }
  },

  getAccessToken: (): string | null => {
    try {
      const cookies = parseCookies();
      const token = cookies[AUTH_COOKIE_NAMES.accessToken];

      if (!token) {
        throw new TokenError('TOKEN_MISSING', 'Access token not found');
      }

      return token;
    } catch {
      // console.error("Error getting access token:", error);
      return null;
    }
  },

  decodeJWT: <T extends AccessTokenDTO = AccessTokenDTO>(): T | null => {
    try {
      const accessToken = tokenManager.getAccessToken();

      if (!accessToken) {
        throw new TokenError('TOKEN_MISSING', 'Access token not found');
      }

      const decoded = jwtDecode<T>(accessToken);

      // Validate token expiration
      if (decoded.exp * 1000 < Date.now()) {
        throw new TokenError('TOKEN_EXPIRED', 'Token has expired');
      }

      return decoded;
    } catch (error) {
      if (error instanceof TokenError) {
        // console.error("Token error:", error.message);
      } else {
        // console.error("Error decoding JWT:", error);
      }
      return null;
    }
  },

  // Check if user is deactivated and remove tokens if so
  checkUserDeactivation: async (): Promise<boolean> => {
    try {
      const userId = tokenManager.getUserId();

      if (!userId) {
        return false;
      }

      const user = await userApi.getUserMe();

      // If user is deactivated, remove all tokens
      if (user.deactivatedAt !== null) {
        tokenManager.removeTokens();
        logoutUser(false, true);
        return true; // User is deactivated
      }

      return false; // User is active
    } catch (error) {
      // If we can't check user status, assume they're still active
      // This prevents blocking legitimate users due to network issues
      console.warn('Could not check user deactivation status:', error);
      return false;
    }
  },

  getRefreshToken: (): string | null => {
    try {
      const cookies = parseCookies();
      const token = cookies[AUTH_COOKIE_NAMES.refreshToken];

      if (!token) {
        throw new TokenError('TOKEN_MISSING', 'Refresh token not found');
      }

      return token;
    } catch {
      // console.error("Error getting refresh token:", error);
      return null;
    }
  },

  getTokenExpiry: (): number | null => {
    try {
      const cookies = parseCookies();
      const expiry = cookies[AUTH_COOKIE_NAMES.tokenExpiry];

      if (!expiry) {
        throw new TokenError('TOKEN_MISSING', 'Token expiry not found');
      }

      return Number.parseInt(expiry, 10);
    } catch {
      // console.error("Error getting token expiry:", error);
      return null;
    }
  },

  isTokenExpired: (): boolean => {
    try {
      const decoded = tokenManager.decodeJWT();
      if (!decoded) {
        return true;
      }

      // Add a 30-second buffer to handle time differences
      const bufferTime = 30 * 1000; // 30 seconds in milliseconds
      return Date.now() >= decoded.exp * 1000 - bufferTime;
    } catch {
      // console.error("Error checking token expiration:", error);
      return true;
    }
  },

  /**
   * Remove tokens for both cookie variants to avoid leaving stale cookies behind
   * if a user was previously assigned a different cookie strategy.
   */
  removeTokens: () => {
    try {
      // Clear cookies set with the default options
      destroyCookie(null, AUTH_COOKIE_NAMES.accessToken, COOKIE_OPTIONS);
      destroyCookie(null, AUTH_COOKIE_NAMES.refreshToken, COOKIE_OPTIONS);
      destroyCookie(null, AUTH_COOKIE_NAMES.tokenExpiry, COOKIE_OPTIONS);

      // And also clear any cookies that might have been set with the alt options
      destroyCookie(null, AUTH_COOKIE_NAMES.accessToken, COOKIE_OPTIONS_OUTDATED);
      destroyCookie(null, AUTH_COOKIE_NAMES.refreshToken, COOKIE_OPTIONS_OUTDATED);
      destroyCookie(null, AUTH_COOKIE_NAMES.tokenExpiry, COOKIE_OPTIONS_OUTDATED);
      return true;
    } catch {
      // console.error("Error removing tokens:", error);
      return false;
    }
  },

  getUserId: (): string | null => {
    try {
      const decoded = tokenManager.decodeJWT();
      return decoded?.sub || null;
    } catch {
      // console.error("Error getting user ID:", error);
      return null;
    }
  },

  getSecurityToken: (): string | null => {
    try {
      const cookies = parseCookies();
      const token = cookies[AUTH_COOKIE_NAMES.securityToken];

      if (!token) {
        throw new TokenError('TOKEN_MISSING', 'Security token not found');
      }

      return token;
    } catch {
      return null;
    }
  },
};
